import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { errMsg, useAdminApi } from "@/lib/api";

const toNum = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export default function PrescriptionCell({ booking }) {
  const call = useAdminApi();
  const inp = useRef(null);
  const [has, setHas] = useState(!!booking.prescription);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { setHas(!!booking.prescription); }, [booking.id, booking.prescription]);

  const fetchBlob = useCallback(() => call("get", `/admin/bookings/${booking.id}/prescription`, null,
    { responseType: "blob" }), [booking.id, call]);

  const view = useCallback(async () => {
    const popup = window.open("about:blank", "_blank");
    try {
      const blob = await fetchBlob();
      const u = URL.createObjectURL(blob);
      if (popup) popup.location.href = u;
      else window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 120000);
    } catch (e) {
      if (popup) popup.close();
      toast.error(errMsg(e, "Could not open prescription"));
    }
  }, [fetchBlob]);

  const sendToPatient = useCallback(async () => {
    setSending(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], `JoyfulMinds-prescription-${booking.ref}.jpg`, { type: "image/jpeg" });
      const num = toNum(booking.phone);
      const text = `Hi ${booking.name}, here is your prescription from Joyful Minds (Ref ${booking.ref}, ${booking.date}). Take care.`;
      // Mobile: share sheet → WhatsApp attaches the image directly
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text, title: `Joyful Minds prescription - ${booking.ref}` });
          return;
        } catch (err) {
          if (err.name === "AbortError") return;
        }
      }
      // Desktop fallback: auto-download + open WhatsApp chat
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      window.open(
        `https://wa.me/${num}?text=${encodeURIComponent(text + "\n\n(Prescription downloaded - please attach it in this chat.)")}`,
        "_blank",
      );
    } catch (e) {
      toast.error(errMsg(e, "Could not send prescription"));
    } finally { setSending(false); }
  }, [fetchBlob, booking.ref, booking.phone, booking.name, booking.date]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error("Max 15 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await call("post", `/admin/bookings/${booking.id}/prescription`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHas(true);
      toast.success("Prescription uploaded (compressed).");
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Replace / remove this prescription?")) return;
    try {
      await call("delete", `/admin/bookings/${booking.id}/prescription`);
      setHas(false);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input ref={inp} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
             data-testid={`prescription-input-${booking.ref}`} onChange={onFile} />
      {has ? (
        <>
          <Button size="sm" variant="secondary" onClick={view} data-testid={`prescription-view-${booking.ref}`} className="h-7 rounded-full px-2 text-xs">
            View
          </Button>
          <Button size="sm" onClick={sendToPatient} disabled={sending}
                  data-testid={`prescription-send-${booking.ref}`}
                  className="h-7 rounded-full bg-[#25D366] px-2 text-xs text-[#06120e] hover:bg-[#3ee07c]">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="mr-1 h-3 w-3" /> Send</>}
          </Button>
          <Button size="sm" variant="ghost" onClick={del} data-testid={`prescription-remove-${booking.ref}`} className="h-7 rounded-full px-2 text-xs text-red-200 hover:bg-red-500/15">
            <X className="h-3 w-3" />
          </Button>
        </>
      ) : (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => inp.current?.click()}
                data-testid={`prescription-upload-${booking.ref}`} className="h-7 rounded-full px-2 text-xs">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Camera className="mr-1 h-3 w-3" /> Upload</>}
        </Button>
      )}
    </div>
  );
}
